// src/pages/Events.js
import React, { useState, useEffect } from 'react';
import { MapPin, ArrowRight, Calendar, Loader, AlertTriangle, Plus, X, ExternalLink } from 'lucide-react';
import { fetchEvents } from '../utils/airtable';
import './Events.css';

// If the embed hasn't fired its onLoad event within this window, assume it's
// blocked and fall back to a direct link to the regular form.
const IFRAME_LOAD_TIMEOUT_MS = 10000;

const SubmitEventModal = ({ formUrl, embedUrl, countyName, onClose }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(current => {
        if (!current) setFailed(true);
        return current;
      });
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="submit-event-overlay" onClick={onClose}>
      <div
        className="submit-event-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Submit an event for ${countyName}`}
        onClick={e => e.stopPropagation()}
      >
        <button className="submit-event-close" onClick={onClose} aria-label="Close">
          <X size={20} color="currentColor" />
        </button>

        <div className="submit-event-body">
          {!loaded && !failed && (
            <div className="submit-event-loading">
              <Loader size={28} color="currentColor" className="submit-event-spinner" />
              <div>Loading form…</div>
            </div>
          )}

          {failed && (
            <div className="submit-event-fallback">
              <AlertTriangle size={28} color="currentColor" />
              <div>The form couldn't be loaded here.</div>
              <a className="btn-navy submit-event-btn" href={formUrl} target="_blank" rel="noreferrer">
                Open form in a new tab <ExternalLink size={16} color="currentColor" style={{ verticalAlign: 'middle' }} />
              </a>
            </div>
          )}

          {!failed && (
            <iframe
              className="airtable-embed"
              src={embedUrl}
              frameBorder="0"
              width="100%"
              height="533"
              style={{ background: 'transparent', border: '1px solid #ccc', display: loaded ? 'block' : 'none' }}
              title={`Submit an event — ${countyName}`}
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const EventImageModal = ({ url, alt, onClose }) => {
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="event-image-overlay" onClick={onClose}>
      <button className="event-image-close" onClick={onClose} aria-label="Close">
        <X size={20} color="currentColor" />
      </button>
      <img className="event-image-full" src={url} alt={alt} onClick={e => e.stopPropagation()} />
    </div>
  );
};

const EventCard = ({ event }) => {
  const [showImageModal, setShowImageModal] = useState(false);

  // Parse YYYY-MM-DD as local date to avoid UTC timezone shift
  const date = event.Date
    ? (() => {
        const [year, month, day] = event.Date.split('-').map(Number);
        return new Date(year, month - 1, day);
      })()
    : null;
  const month = date ? MONTH_ABBR[date.getMonth()] : '—';
  const day = date ? date.getDate() : '—';

  const image = event.Image && event.Image.length > 0 ? event.Image[0] : null;
  const thumbUrl = image?.thumbnails?.large?.url || image?.url;

  return (
    <div className="event-item">
      <div className="event-date">
        <div className="event-month">{month}</div>
        <div className="event-day">{day}</div>
      </div>
      <div className="event-body">
        <div className="event-title">{event.Title}</div>
        <div className="event-sub">
          <MapPin size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> {event['Location Name']}{event.City ? `, ${event.City}` : ''}
          {event.Time && ` · ${event.Time}`}
        </div>
        {event.Description && <div className="event-desc">{event.Description}</div>}
        {event['RSVP Link'] && (
          <a className="event-rsvp" href={event['RSVP Link']} target="_blank" rel="noreferrer">RSVP <ArrowRight size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /></a>
        )}
      </div>
      {image && (
        <img
          className="event-thumb"
          src={thumbUrl}
          alt={event.Title}
          onClick={() => setShowImageModal(true)}
        />
      )}
      {showImageModal && (
        <EventImageModal url={image.url} alt={event.Title} onClose={() => setShowImageModal(false)} />
      )}
    </div>
  );
};

const Events = ({ county }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  useEffect(() => {
    fetchEvents()
      .then(data => { setEvents(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  return (
    <div>
      <div className="sec-head">
        <div className="sec-icon"><Calendar size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
        <div>
          <div className="sec-title">Upcoming events</div>
          <div className="sec-desc">Forums, drives, and GOP events in {county?.name || 'Larimer County'}</div>
        </div>
      </div>

      {loading && <div className="loading"><Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Loading events...</div>}
      {error && <div className="error-msg"><AlertTriangle size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Could not load events. Please check your Airtable connection.</div>}

      {!loading && !error && events.length === 0 && (
        <div className="no-results" style={{padding:16,background:'#f5f5f5',borderRadius:6,fontSize:13,color:'#666'}}>
          No upcoming events found. Check back soon or contact the {county?.name || 'Larimer County'} GOP office.
        </div>
      )}

      {!loading && !error && events.map(e => <EventCard key={e.id} event={e} />)}

      {county?.eventSubmitEmbedUrl && (
        <div style={{ marginTop: 16 }}>
          <button className="btn-navy submit-event-btn" onClick={() => setShowSubmitModal(true)}>
            <Plus size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Submit an Event
          </button>
        </div>
      )}

      {showSubmitModal && (
        <SubmitEventModal
          formUrl={county.eventSubmitFormUrl}
          embedUrl={county.eventSubmitEmbedUrl}
          countyName={county?.name}
          onClose={() => setShowSubmitModal(false)}
        />
      )}
    </div>
  );
};

export default Events;
